import { withNamespaces } from "react-i18next"
import {
    Col,
    Row,
    Button,
    Progress,
    Spinner,
} from "reactstrap"
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import Accordion from "react-bootstrap/Accordion";
import {
    SimpleQuestionnaire,
    useProgress
} from '@smartintegrity/questionnaire';
import { useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import OtherAssetProcessingActivityService from "src/modules/data-protection/apis/OtherAssetProcessingActivityService";
import CommonService from "src/modules/data-protection/apis/CommonService";

const QuestionsLoadingPlaceholder = () => {
    return (
        <div>
            <Row className="mb-2">
                <Col lg="12">
                    <p style={{ width: '100%', height: '55px' }} className={`dt-field dt-skeleton`}></p>
                </Col>
            </Row>
        </div>
    );
}

const ProcessingQuestionnaire = ({
    index,
    report,
    token,
    assetId,
    setActivityToEdit,
    setActivityToDelete,
    t
}) => {
    const [questionnaireCompleted, setQuestionnaireCompleted] = useState(false);

    const fetchCountriesQuery = useQuery({
        queryKey: ["dpdr-processing-activity-countries"],
        queryFn: async () => {
            const service = CommonService.getInstance();

            return await service.countries();
        },
        cacheTime: 0,
        refetchOnWindowFocus: false,
        onError: (error) => {
            if (process.env.NODE_ENV === "development") console.error(error);

            toast(t("An error occurred while fetching countries."), {
                type: "error"
            });
        },
    });

    const {
        control: controlQuestionnaireProgress,
        register: registerQuestionnaireForProgress
    } = useProgress();

    const extraData = useMemo(() => {
        return {
            countries: {
                isFetching: fetchCountriesQuery.isFetching,
                data: (fetchCountriesQuery.data || []).map((country) => {
                    return {
                        ...country,
                        title: country.name
                    }
                })
            }
        }
    }, [
        fetchCountriesQuery.data,
        fetchCountriesQuery.isFetching
    ]);

    const updateActivityMutation = useMutation({
        mutationFn: async (payload) => {
            const service = OtherAssetProcessingActivityService.getInstance();
            return await service.updateActivityQuestionnaire(payload)
        },
        onSuccess() {
            toast(t("Report Updated"), {
                type: 'success'
            });
        },
        onError(error) {
            if (process.env.NODE_ENV === 'development') console.error(error)
            toast(t('Faild to update'), {
                type: "error"
            })
        }
    });

    const initActivityMutation = useMutation({
        mutationFn: async (payload) => {
            const service = OtherAssetProcessingActivityService.getInstance();
            return await service.initActivityQuestionnaire(payload)
        },
        onSuccess() {
            toast(t("Report Updated"), {
                type: 'success'
            });
        },
        onError(error) {
            if (process.env.NODE_ENV === 'development') console.error(error)
            toast(t('Faild to update'), {
                type: "error"
            })
        }
    });

    useEffect(() => {
        if (report?.case_id) {
            registerQuestionnaireForProgress(report.case_id);
        }
    }, [
        report?.case_id,
        registerQuestionnaireForProgress
    ]);

    const questionnaireStatusChangeCallback = useCallback((_currentPayload, newStatus) => {
        setQuestionnaireCompleted(newStatus)
    }, []);

    const handleSubmitQuestionnaire = useCallback((payload) => {
        if (report?.case_id) {
            if (!report.is_empty) {
                updateActivityMutation.mutate({
                    questionAnswers: payload,
                    software: null,
                    hardware: null,
                    reportCase: report.case_id,
                    questionnaireType: 'other_asset_processing_activity'
                });
            }
            else {
                initActivityMutation.mutate({
                    questionAnswers: payload,
                    reportCase: report.case_id
                })
            }
        }
    }, [
        report,
        updateActivityMutation,
        initActivityMutation,
        // assetId
    ]);

    const settings = useMemo(() => {
        const updateInProcess = updateActivityMutation.isLoading || initActivityMutation.isLoading;

        let submitButtonSettings = {
            enable: (
                !updateInProcess
            ),
            title: updateInProcess ? (
                <>
                    <Spinner
                        size='sm'
                        color="white"
                        className="me-1"
                    />
                    {`${t('Saving')}...`}
                </>
            ) : t('Update')
        }

        return {
            panelSettings: {
                showNumber: true,
                showProgressBar: false,
                prefix: `${index + 1}.`,
            },
            buttonsSettings: {
                submit: {
                    display: true,
                    ...submitButtonSettings
                },
                back: {
                    display: false
                }
            },
            fileUploaderOptions: {
                deleteRequestOptions: {
                    headers: {
                        Authorization: `Bearer ${token}`
                    },
                    payload: {},
                    requestUrl: ({
                        questionId,
                        file
                    }) => {
                        return ``
                    }
                },
                uploadRequestOptions: {
                    headers: {
                        Authorization: `Bearer ${token}`
                    },
                    payload: {},
                    requestUrl: ({
                        questionId
                    }) => {
                        return ``
                    }
                }
            },
            questions: {
                showNumber: true
            }
        }
    }, [
        token,
        t,
        index,
        updateActivityMutation.isLoading,
        initActivityMutation.isLoading
    ]);

    const isQuestionnaireRegistered = controlQuestionnaireProgress.progressQuestionnaires.findIndex(q => q.id === report.case_id) !== -1;

    if (!isQuestionnaireRegistered) {
        return (
            <QuestionsLoadingPlaceholder />
        )
    }

    const questionnaireTabsProgressState = controlQuestionnaireProgress.progressQuestionnaires.find(q => q.id === report.case_id);

    const requiredQuestions = questionnaireTabsProgressState?.tabs.reduce((acc, tab) => {
        return acc + (tab.required || 0);
    }, 0) || 0;
    const requiredAnsweredQuestionsCount = questionnaireTabsProgressState?.tabs.reduce((acc, tab) => {
        return acc + (tab.requiredAnswered || 0);
    }, 0) || 0;

    const optionalQuestions = questionnaireTabsProgressState?.tabs.reduce((acc, tab) => {
        return acc + (tab.optional || 0);
    }, 0) || 0;
    const optionalAnsweredQuestionsCount = questionnaireTabsProgressState?.tabs.reduce((acc, tab) => {
        return acc + (tab.optionalAnswered || 0);
    }, 0) || 0;


    let tabRequiredPercent = 0
    let tabOptionalPercent = 0

    if (requiredQuestions) {
        tabRequiredPercent = (requiredAnsweredQuestionsCount / requiredQuestions * 100).toFixed(0)
    }

    if (optionalQuestions) {
        tabOptionalPercent = (optionalAnsweredQuestionsCount / optionalQuestions * 100).toFixed(0)
    }

    return (
        <Accordion.Item
            className="border-none mb-2"
            eventKey={index}
        >
            <div className="d-flex justify-content-between align-items-center p-3 bg-white">
                <Accordion.Button
                    className="bg-white"
                >
                    <div className="d-flex align-items-center justify-content-between w-100">
                        <div>
                            <span className="me-1">
                                {index + 1}.
                            </span>
                            {report.module_name}
                        </div>

                        <div className="completed-percent">
                            <div className={`required-questions ${!requiredQuestions ? 'd-none' : ''}`}>
                                <div className='titles'>
                                    <span className='group-title'>
                                        {`${t('Mandatory')}: ${requiredAnsweredQuestionsCount}/${requiredQuestions}`}
                                    </span>
                                    <span className='percent-title'>
                                        {tabRequiredPercent >= 100 ? t('Completed') : `${tabRequiredPercent}%`}
                                    </span>
                                </div>
                                <Progress
                                    className={tabRequiredPercent >= 100 ? 'completed' : 'incompleted'}
                                    color="success"
                                    value={tabRequiredPercent}
                                />
                            </div>

                            <div className={`optional-questions ${!optionalQuestions ? 'd-none' : ''}`}>
                                <div className='titles'>
                                    <span className='group-title'>
                                        {`${t('Optional')}: ${optionalAnsweredQuestionsCount}/${optionalQuestions}`}
                                    </span>
                                    <span className='percent-title'>
                                        {tabOptionalPercent >= 100 ? t('Completed') : `${tabOptionalPercent}%`}
                                    </span>
                                </div>
                                <Progress
                                    className={tabOptionalPercent >= 100 ? 'completed' : 'incompleted'}
                                    color="success"
                                    value={tabOptionalPercent}
                                />
                            </div>
                        </div>
                    </div>
                </Accordion.Button>
                <div className="d-flex justify-conent-center align-items-center">
                    <Button
                        outline
                        color="primary"
                        className="border-0"
                        onClick={() => setActivityToEdit(report)}
                    >
                        <i class="ri-pencil-line font-size-18"></i>
                    </Button>

                    <Button
                        outline
                        color="danger"
                        className="border-0"
                        onClick={() => setActivityToDelete(report.case_id)}
                    >
                        <i class="ri-delete-bin-line font-size-18"></i>
                    </Button>
                </div>
            </div>
            <Accordion.Body className="pt-3">
                <SimpleQuestionnaire
                    questionnaireUniqueId={report.case_id}
                    className='gdpr'
                    questions={report.questions || []}
                    defaultValues={report.mappedAnswers}
                    settings={settings}
                    onQuestionnaireCompleteStatusChanged={questionnaireStatusChangeCallback}
                    onSubmit={handleSubmitQuestionnaire}
                    extraData={extraData}
                    {...controlQuestionnaireProgress}
                />
            </Accordion.Body>
        </Accordion.Item>
    );
}

const mapStatetoProps = (state) => {
    const { token, user } = state.Login;

    return {
        token,
        user
    };
};

export default withNamespaces()(
    connect(mapStatetoProps, {})(
        ProcessingQuestionnaire
    )
)